import { Navigate } from 'router-v6'
import { CLIAuthorize } from 'v2/pages/auth/cli-authorize'
import { CompleteSignup } from 'v2/pages/auth/complete-signup'
import { ForgotPasswordV2 } from 'v2/pages/auth/forgot-password-v2'
import { InvitationsV2 } from 'v2/pages/auth/invitations-v2'
import { InviteSignup } from 'v2/pages/auth/invite-signup'
import { Login } from 'v2/pages/auth/login'
import { LoginV2 } from 'v2/pages/auth/login-v2'
import { ResendActivationV2 } from 'v2/pages/auth/resend-activation-v2'
import { ResetPasswordV2 } from 'v2/pages/auth/reset-password-v2'
import { SignupV2 } from 'v2/pages/auth/signup-v2'
import { SsoRedirect } from 'v2/pages/auth/sso-redirect'
import { TwoFactorAuth } from 'v2/pages/auth/two-factor-auth'
import TwoFactorVerify from 'v2/pages/auth/two-factor-verify'
import { createLazyComponent } from 'v2/shared/utils/lazy-utils'
import type { IRoute } from 'v2/types'

// Lazy-loaded components
const ReceiptView = createLazyComponent(() => import('v2/pages/billing/components/receipt-view'))
const PaymentCallback = createLazyComponent(
  () => import('v2/pages/billing/components/payment-callback'),
)
// Embed iframe pages: shell-less; auth comes from the embed session, never the console login.
const EmbedAgents = createLazyComponent(() => import('v2/pages/embed/embed-agents'))
const EmbedBuilder = createLazyComponent(() => import('v2/pages/embed/embed-builder'))
const EmbedRunDetail = createLazyComponent(() => import('v2/pages/embed/embed-run-detail'))
const EmbedNotFound = createLazyComponent(() => import('v2/pages/embed/embed-not-found'))
const PortOutConsent = createLazyComponent(() => import('v2/pages/port-out/consent'))

export const openRoutes: IRoute[] = [
  {
    // Reached from an email by a logged-out customer; the `token` in the link is the credential.
    title: 'Port-out request',
    path: '/number/port-out/response',
    component: <PortOutConsent />,
    hidden: true,
  },
  {
    title: 'Login',
    path: '/login-v1',
    component: <Login />,
  },
  {
    title: 'Login',
    path: '/login',
    component: <LoginV2 />,
  },
  {
    title: 'Signup',
    path: '/signup',
    component: <SignupV2 />,
  },
  {
    title: 'Signup Redirect',
    path: '/pungis2',
    component: <Navigate to="/signup" replace />,
    hidden: true,
  },
  {
    title: 'Complete Signup',
    path: '/complete-signup',
    component: <CompleteSignup />,
  },
  {
    // Plivo CLI loopback-OAuth (PKCE) browser-login consent screen. Hodor's
    // /v1/accounts/cli/authorize redirects here with ?state=…. Page lives in
    // openRoutes because the user may not yet be logged in — the component
    // itself redirects to /login?next=… in that case.
    title: 'Authorize Plivo CLI',
    path: '/cli/authorize',
    component: <CLIAuthorize />,
  },
  {
    title: 'Two-Factor Authentication',
    path: '/two-factor-auth',
    component: <TwoFactorAuth />,
  },
  {
    title: 'Two-Factor Verification',
    path: '/two-factor-verify',
    component: <TwoFactorVerify />,
  },
  {
    title: 'Forgot password',
    path: '/forgot-password',
    component: <ForgotPasswordV2 />,
  },
  {
    title: 'Resend activation email',
    path: '/resend-activation-email',
    component: <ResendActivationV2 />,
  },
  {
    title: 'Invitations',
    path: '/invitations',
    component: <InvitationsV2 />,
  },
  {
    title: 'Invite Signup',
    path: '/invite-signup',
    component: <InviteSignup />,
  },
  {
    title: 'Set password',
    path: '/set-password',
    component: <ResetPasswordV2 />,
  },
  // Add oauth callback route here
  {
    title: 'Google redirect',
    path: '/google-login',
    hidden: true,
    component: <SsoRedirect />,
  },
  {
    title: 'Azure redirect',
    path: '/azure-login',
    hidden: true,
    component: <SsoRedirect />,
  },
  {
    title: 'Okta redirect',
    path: '/okta-login',
    hidden: true,
    component: <SsoRedirect />,
  },
  {
    title: 'Auth0 redirect',
    path: '/auth0-login',
    hidden: true,
    component: <SsoRedirect />,
  },
  {
    title: 'WorkOS redirect',
    path: '/workos-login',
    hidden: true,
    component: <SsoRedirect />,
  },
  {
    title: 'Receipt',
    path: '/billing/receipt/:uuid',
    component: <ReceiptView />,
    hidden: true,
  },
  {
    title: 'Payment Callback',
    path: '/payment/callback',
    component: <PaymentCallback />,
    hidden: true,
  },
  {
    title: 'Embedded Agents',
    path: '/embed/agents',
    component: <EmbedAgents />,
    hidden: true,
  },
  {
    title: 'Embedded Agent Builder',
    path: '/embed/agent/builder/:id/*',
    component: <EmbedBuilder />,
    hidden: true,
  },
  {
    title: 'Embedded Agent Run Detail',
    path: '/embed/agent-runs/:flowId/:runId',
    component: <EmbedRunDetail />,
    hidden: true,
  },
  {
    // Unknown /embed/* paths get a neutral error, never the console login.
    title: 'Embedded Not Found',
    path: '/embed/*',
    component: <EmbedNotFound />,
    hidden: true,
  },
]
